import React from 'react';
import { cn } from '../../lib/utils';
import { ScreenHelp } from './ScreenHelp';

/**
 * PageHeader - Componente padrão para headers de páginas de viagem
 * 
 * Layout Mobile:
 * - Linha 1: Título centralizado
 * - Linha 2: Subtítulo centralizado
 * - Linha 3: [Ajuda] [Ações] - Ajuda à esquerda, Ações à direita
 * 
 * Layout Desktop:
 * - Linha 1: Título à esquerda, Ações à direita
 * - Linha 2: Subtítulo
 * 
 * Props:
 * - title: Título da página (obrigatório)
 * - subtitle: Subtítulo/descrição da página (opcional)
 * - badge: ReactNode badge a ser exibido ao lado do título (opcional)
 * - helpScreen: Nome da tela para o componente ScreenHelp (opcional)
 * - actions: ReactNode com os botões de ação (opcional)
 * - className: Classes CSS adicionais (opcional)
 */
export function PageHeader({ 
  title, 
  subtitle, 
  badge,
  helpScreen, 
  actions,
  className 
}) {
  return (
    <div className={cn("mb-6", className)}>
      {/* Desktop Layout */}
      <div className="hidden sm:block space-y-3">
        {/* Row 1: Title + Badge + Help + Actions */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-slate-900 dark:text-white">
              {title}
            </h2>
            {badge}
            {helpScreen && (
              <ScreenHelp screen={helpScreen} />
            )}
          </div>
          
          {actions && (
            <div className="flex flex-wrap items-center gap-2">
              {actions}
            </div>
          )}
        </div>
        
        {/* Row 2: Subtitle */}
        {subtitle && (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {subtitle}
          </p>
        )}
      </div>
      
      {/* Mobile Layout - Centralizado */}
      <div className="sm:hidden space-y-3">
        {/* Row 1: Title centered */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2">
            <h2 className="font-heading font-bold text-xl text-slate-900 dark:text-white">
              {title}
            </h2>
            {badge}
          </div>
        </div>
        
        {/* Row 2: Subtitle centered */}
        {subtitle && (
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center">
            {subtitle}
          </p>
        )}
        
        {/* Row 3: Help left, Actions right - Botões com tamanho uniforme */}
        {(helpScreen || actions) && (
          <div className="flex items-center justify-between gap-2 pt-1 [&>*]:flex-shrink-0 [&_button]:h-10 [&_button]:px-4 [&_button]:text-sm">
            <div>
              {helpScreen && (
                <ScreenHelp screen={helpScreen} />
              )}
            </div>
            <div className="flex items-center gap-2">
              {actions}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PageHeader;
